<template>
  <div class="vp-input-box vp-datetime-input">
    <Input
      v-bind="$attrs"
      :input-id="inputId"
    >
      <template #default="{ uid, hasError, errors, onInput: onFieldInput }">
        <VPLabel v-if="label">
          {{ label }}
        </VPLabel>

        <div class="flex flex-row">
          <template v-if="showNativeDateTimePickers">
            <input
              :id="`${uid}-date`"
              type="date"
              :value="nativeDateDisplay(dateTime?.toJSDate())"
              :placeholder="$gettext('Select date')"
              class="mr-8"
              @input="onDateUpdate(onNativeDateInput($event), onFieldInput)"
            >
            <input
              :id="`${uid}-time`"
              type="time"
              :value="nativeTimeDisplay(timeModelValue)"
              class=""
              :placeholder="$gettext('Select time')"
              @input="onTimeUpdate(onNativeTimeInput($event), onFieldInput)"
            >
          </template>
          <template v-else>
            <VueDatePicker
              v-if="!hideDate"
              :id="`${uid}-date`"
              :format="pickerDateFormat"
              :week-start="0"
              :show-now-button="showNowButton"
              :model-value="dateTime?.toJSDate()"
              :placeholder="placeholder"
              :auto-apply="true"
              :clearable="clearable"
              :enable-time-picker="false"
              :allowed-dates="allowedDates"
              :disabled-dates="disabledDates"
              :no-today="noToday"
              :timezone="timezone"
              :disabled="disabledDate"
              :readonly="readonlyDate"
              :text-input="textInput"
              @update:model-value="onDateUpdate($event, onFieldInput)"
            >
              <template #input-icon />
              <template
                v-if="showNowButton"
                #action-extra
              >
                <VPButton
                  style="font-size: 14px"
                  variant="outline"
                  max-width="100%"
                  size="xs"
                  @click="selectCurrentTimeInTimezone"
                >
                  Right Now
                </VPButton>
              </template>
            </VueDatePicker>
            <VueDatePicker
              v-if="!hideTime"
              :id="`${uid}-time`"
              time-picker
              enable-time-picker
              no-seconds-overlay
              :is-24="false"
              :model-value="timeModelValue"
              :placeholder="_timePlaceholder"
              :format="pickerTimeFormat"
              :auto-apply="true"
              :clearable="clearable"
              :disabled="disabledTime"
              :readonly="readonlyTime"
              :text-input="textInput"
              :mode-height="modeHeight"
              :timezone="timezone"
              @update:model-value="onTimeUpdate($event, onFieldInput)"
            >
              <template #input-icon />
            </VueDatePicker>
          </template>
        </div>
        <span
          v-if="hasError"
          class="text-vp-error font-bold text-xs"
        >{{ errors.join(', ') }}</span>
      </template>
    </Input>
  </div>
</template>

<script setup lang="ts">
import UAParser from 'ua-parser-js';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { Input, useFormsStore } from 'vuniform';
import { computed, inject } from 'vue';
import { DateTime } from 'luxon';
import VPButton from '@/components/form/VPButton.vue';
import VPLabel from '@/components/form/VPLabel.vue';
import { useGettext } from 'vue3-gettext';

interface TimeModel {
  hours: number;
  minutes: number;
  seconds: number;
}

interface Props {
  label?: string;
  placeholder?: string;
  timePlaceholder?: string;
  hideTime?: boolean;
  hideDate?: boolean;
  timezone?: string;
  skipVuniform?: boolean;
  inputId: string;
  clearable?: boolean;
  pickerDateFormat?: string;
  pickerTimeFormat?: string;
  showNowButton?: boolean;
  disabledDates?: Date[];
  allowedDates?: Date[];
  noToday?: boolean;
  readonlyDate?: boolean;
  readonlyTime?: boolean;
  disabledDate?: boolean;
  disabledTime?: boolean;
  textInput?: boolean;
  modeHeight?: number;
  useNativeDateTimePickers?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  pickerDateFormat: 'LLL dd, yyyy',
  pickerTimeFormat: 'h:mm a',
  showNowButton: true,
  useNativeDateTimePickers: false,
});

const emit = defineEmits<{
  (e: 'update', value: string, cb: (v: string) => void): void;
}>();

const { $gettext } = useGettext();

const formsStore = useFormsStore();
const formId: string = inject('formId')!;

const dateTime = computed<DateTime | null>(() => {
  const value = <string>formsStore.fieldGetValue(formId, props.inputId);
  if (!value) {
    return null;
  }
  return DateTime.fromISO(value, { zone: props.timezone });
});

const timeModelValue = computed(() => {
  if (!dateTime.value) {
    return null;
  }
  return {
    hours: dateTime.value.hour,
    minutes: dateTime.value.minute,
    seconds: dateTime.value.second,
  } as TimeModel;
});

const _timePlaceholder = computed(() => props.timePlaceholder || props.placeholder);

const showNativeDateTimePickers = computed(
  () => props.useNativeDateTimePickers && ['Android', 'Android-x86', 'iOS'].includes(new UAParser().getOS()?.name),
);

const onUpdate = (dt: DateTime | null, cb: (e: { target: { value: unknown } }) => void) => {
  const value = dt && dt.setZone(props.timezone, { keepLocalTime: true }).toISO();

  if (cb && !props.skipVuniform) {
    cb({ target: { value } });
  }
  emit('update', value, v => cb && cb({ target: { value: v } }));
};

const onDateUpdate = (d: Date | null, cb: (e: { target: { value: unknown } }) => void) => {
  if (!d) {
    onUpdate(null, cb);
    return;
  }
  const dt = DateTime.fromJSDate(d);
  const newDate = (dateTime.value || DateTime.now()).set({ year: dt.year, month: dt.month, day: dt.day });
  onUpdate(newDate, cb);
};

const onTimeUpdate = (d: TimeModel | null, cb: (e: { target: { value: unknown } }) => void) => {
  if (!d) {
    onUpdate(null, cb);
    return;
  }
  const updatedTime = {
    hour: d.hours,
    minute: d.minutes,
    second: 0,
    millisecond: 0,
  };
  const newDate = (dateTime.value || DateTime.now()).set(updatedTime);
  onUpdate(newDate, cb);
};

const nativeDateDisplay = (date: Date | undefined) => {
  if (!date) return null;
  return DateTime.fromJSDate(date, { zone: props.timezone }).toISODate();
};

const onNativeDateInput = event => new Date(event.target.value);

const nativeTimeDisplay = (time: TimeModel | null) => {
  if (!time) return null;
  const hours = ('0000' + time.hours).slice(-2);
  const minutes = ('0000' + time.minutes).slice(-2);

  return `${hours}:${minutes}`;
};

const onNativeTimeInput = event => {
  const value = event.target.value;
  if (!value) return null;

  const [hours, minutes] = value.split(':');

  return { hours, minutes };
};

const selectCurrentTimeInTimezone = () => {
  const currentTime = DateTime.now();
  formsStore.STAGE_FIELD_CHANGE({
    value: currentTime.minus({ minutes: 1 }),
    query: { formId: formId, field: props.inputId },
  });
};
</script>
